import React, { useEffect, useMemo, useState } from "react";
import { X, Plus, Upload, Image as ImageIcon } from "lucide-react";
import type { CreateCardUserPayload, CreatedUserResponse } from "../api/admin";

type Props = {
  open: boolean;
  onClose: () => void;
  submit: (payload: CreateCardUserPayload) => Promise<CreatedUserResponse>;
  onCreated: (resp: CreatedUserResponse) => void;
};

const TEMPLATES = [
  { id: "1", name: "Professional Classic" },
  { id: "2", name: "Elegant Looking" },
  { id: "3", name: "Modern and Clean" },
  { id: "4", name: "Golden View" },
  { id: "5", name: "Classic Modular" },
];

const initial = {
  name: "",
  email: "",
  company: "",
  position: "",
  phone: "",
  location: "",
  templateId: "1",
};

export default function CreateCardUserModal({ open, onClose, submit, onCreated }: Props) {
  const [form, setForm] = useState(initial);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendInvite, setSendInvite] = useState(true);

  const avatarPreview = useMemo(() => (avatarFile ? URL.createObjectURL(avatarFile) : ""), [avatarFile]);
  const coverPreview = useMemo(() => (coverFile ? URL.createObjectURL(coverFile) : ""), [coverFile]);

  useEffect(() => {
    return () => {
      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
      if (coverPreview) URL.revokeObjectURL(coverPreview);
    };
  }, [avatarPreview, coverPreview]);

  useEffect(() => {
    if (!open) {
      setForm(initial);
      setAvatarFile(null);
      setCoverFile(null);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const onPickFile = (setter: (f: File | null) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setter(f);
  };

  const onDrop = (setter: (f: File | null) => void) => (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0] ?? null;
    setter(f);
  };

  const requiredOk = form.name.trim() && form.email.trim();

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const payload: CreateCardUserPayload = {
        ...form,
        generatePassword: !sendInvite,
      };

      const resp = await submit(payload);
      // show initial password to admin if backend returned it
      if (resp && (resp as any).initialPassword) {
        try { (window as any).toast?.success?.(`Initial password: ${(resp as any).initialPassword}`); } catch {}
        // fallback: alert
        try { if (!(window as any).toast) alert(`Initial password: ${(resp as any).initialPassword}`); } catch {}
      }
      onCreated(resp);
      onClose();
    } catch (e: any) {
      setError(e?.message || "Failed to create user.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b p-5">
          <h3 className="text-lg font-semibold">Create Card User</h3>
          <button type="button" onClick={onClose} className="rounded p-2 hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="grid gap-5 p-5">
          {!!error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
          )}

          {/* Basic details */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Full Name *</label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Email *</label>
              <input
                type="email"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                value={form.email}
                onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                placeholder="jane@company.com"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Company</label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                value={form.company}
                onChange={(e) => setForm((s) => ({ ...s, company: e.target.value }))}
                placeholder="Acme Inc."
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Position</label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                value={form.position}
                onChange={(e) => setForm((s) => ({ ...s, position: e.target.value }))}
                placeholder="Marketing Lead"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Phone</label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                value={form.phone}
                onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
                placeholder="+255 700 000 000"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Location</label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                value={(form as any).location}
                onChange={(e) => setForm((s) => ({ ...s, location: e.target.value }))}
                placeholder="New York, USA"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Template</label>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                value={form.templateId}
                onChange={(e) => setForm((s) => ({ ...s, templateId: e.target.value }))}
              >
                {TEMPLATES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="p-5 border-t">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} />
              <span className="text-sm">Send invite email (user will set their password)</span>
            </label>
          </div>

          {/* Image uploads */}
          <div className="grid gap-5 md:grid-cols-2">
            {/* AVATAR */}
            <div>
              <label className="mb-2 block text-sm font-semibold">Avatar (square)</label>
              <div
                className="flex h-40 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50"
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop(setAvatarFile)}
              >
                {avatarPreview ? (
                  <div className="relative h-full w-full">
                    <img src={avatarPreview} alt="avatar preview" className="h-full w-full rounded-lg object-cover" />
                    <button
                      type="button"
                      onClick={() => setAvatarFile(null)}
                      className="absolute right-2 top-2 rounded bg-white/90 p-1 shadow"
                      title="Remove"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="text-center text-gray-500">
                    <ImageIcon className="mx-auto mb-2 h-8 w-8" />
                    <p className="text-sm">Drag & drop or click to upload</p>
                    <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:opacity-90">
                      <Upload className="h-4 w-4" />
                      Choose file
                      <input type="file" accept="image/*" className="hidden" onChange={onPickFile(setAvatarFile)} />
                    </label>
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">Recommended: 400×400px PNG/JPG</p>
            </div>

            {/* COVER */}
            <div>
              <label className="mb-2 block text-sm font-semibold">Cover (wide)</label>
              <div
                className="flex h-40 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50"
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop(setCoverFile)}
              >
                {coverPreview ? (
                  <div className="relative h-full w-full">
                    <img src={coverPreview} alt="cover preview" className="h-full w-full rounded-lg object-cover" />
                    <button
                      type="button"
                      onClick={() => setCoverFile(null)}
                      className="absolute right-2 top-2 rounded bg-white/90 p-1 shadow"
                      title="Remove"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="text-center text-gray-500">
                    <ImageIcon className="mx-auto mb-2 h-8 w-8" />
                    <p className="text-sm">Drag & drop or click to upload</p>
                    <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:opacity-90">
                      <Upload className="h-4 w-4" />
                      Choose file
                      <input type="file" accept="image/*" className="hidden" onChange={onPickFile(setCoverFile)} />
                    </label>
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">Recommended: 1200×400px PNG/JPG</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t p-5">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="button"
            onClick={handleSubmit}
            disabled={!requiredOk || submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            {submitting ? "Creating..." : "Create User"}
          </button>
        </div>
      </div>
    </div>
  );
}
